import {useEffect, useMemo} from 'react'
import {useParams} from 'react-router-dom'
import {DataHrItem} from '../core/_models'
import {HrColumns} from './columns/_columns'
import {useAppSelector} from '../../../hooks/redux'
import {csrAPI} from '../../../services/CSRService'
import {KTCardBody} from '../../../template/helpers'
import {useTable, ColumnInstance} from 'react-table'
import {CustomHeaderColumn} from './columns/CustomHeaderColumn'
import {TableLoading} from '../../../template/helpers/TableLoading'
import {HeatmapLegend} from './components/HeatmapLegend'
import {shallowEqual} from 'react-redux'
import {useAccount} from '../../../hooks/useAccount'

const HrTable = () => {
  const {appId} = useParams()
  const query = useAppSelector((state) => state.hrFilter.query, shallowEqual)
  const {currentAccountIdForRequest: accountId} = useAccount()
  const [fetch, {data, isLoading}] = csrAPI.useHourlyFetchReportMutation()

  useEffect(() => {
    if (query && query.period) {
      fetch({id: appId!, filters: {...query}, account_id: accountId})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, accountId])

  const dataItems = data ? (data?.hourly as DataHrItem[]) : []

  const values = dataItems.flatMap(({hour, ...days}) => Object.values(days))
  const min = Math.min(...values)
  const max = Math.max(...values)

  const columns = useMemo(() => HrColumns, [])
  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable({
    columns,
    data: dataItems,
  })

  const colorScale = (value: number, min: number, max: number) => {
    if (value === 0) return 'transparent'
    const t = max === min ? 1 : (value - min) / (max - min)
    const opacity = 0.2 + t * 0.8
    return `rgba(0, 158, 247, ${opacity})`
  }

  return (
    <>
      <KTCardBody className='pt-1 pb-4'>
        <HeatmapLegend min={min} max={max} />
        <div className='separator separator-dashed my-3' />
        {isLoading && <TableLoading />}
        <div className='table-responsive'>
          <table
            id='kt_table_users'
            className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
            {...getTableProps()}
          >
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                {headers.map((column: ColumnInstance<DataHrItem>, i) => (
                  <CustomHeaderColumn key={i} column={column} />
                ))}
              </tr>
            </thead>
            <tbody className='text-gray-600 fw-bold' {...getTableBodyProps()}>
              {rows.length > 0 ? (
                rows.map((row) => {
                  prepareRow(row)
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        const value = cell.value as number
                        const bg = colorScale(value, min, max)

                        return (
                          <td
                            className='py-1'
                            {...cell.getCellProps({
                              style: cell.column.id !== 'hour' ? {backgroundColor: bg} : undefined,
                            })}
                          >
                            {cell.render('Cell') as React.ReactNode}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={7}>
                    <div className='d-flex text-center w-100 align-content-center justify-content-center'>---</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </KTCardBody>
    </>
  )
}

export {HrTable}
